import React, { useContext, useState, useCallback } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import DescriptionIcon from '@mui/icons-material/Description';
import { SiteContext } from '../utils/SiteContext';
import { GetLogout } from '../utils/AxiosUtils';
import AppHeader from './TopAppBar';
import { PageContentArea } from './PageContentArea';

const drawerWidth = 200;
const collapsedWidth = 64;

/** Mixins copied from the mini variant pattern */
const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  background: theme.palette.primary.dark,
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  // 56px-ish rail (theme.spacing(7) = 56) + 1px
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  background: theme.palette.primary.dark,
});

/** Drawer paper sits below a 60px app bar */
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: 60, // matches your app bar height
  padding: theme.spacing(0, 1),
}));

/** Styled Drawer identical to the mini variant approach */
const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    position: 'relative',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
      top: 60, // keeps your 60px offset
      background: theme.palette.primary.dark,
      color: theme.palette.primary.contrastText,
      borderRight: 'none', // optional: cleaner look
    },
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': {
        ...openedMixin(theme),
      },
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': {
        ...closedMixin(theme),
      },
    }),
  })
);

/** Bottom section that hugs the bottom whether open or closed */
const BottomContainer = styled('div')(({ theme }) => ({
  marginTop: 'auto',
}));


const Main = styled('main', {
  shouldForwardProp: (prop) => prop !== 'open' && prop !== 'isMobile',
})(({ theme, open, isMobile }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100dvh',
  height: '100vh',
  flexGrow: 1,
  padding: theme.spacing(3),

  // Only animate layout shifts on non-mobile
  transition: isMobile
    ? 'none'
    : theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),

  ...(isMobile
    ? {
        // On mobile, the main content fills the viewport and never shifts
        marginLeft: 0,
        width: '100vw', // or '100vw' if you need viewport-based sizing
        // Optional: prevent accidental horizontal scroll when overlay animates in
        overflowX: 'hidden',
      }
    : {
        // On desktop/tablet, respect the drawer width
        marginLeft: open ? drawerWidth : collapsedWidth,
        width: open
          ? `calc(100vw - ${drawerWidth}px)`
          : `calc(100vw - ${collapsedWidth}px - 20px)`,
      }),
}));



export default function ClippedDrawer({children}) {
  const { loading, userInfo, tab, alert, custCompany } = useContext(SiteContext);
  const [, setLoading] = loading;
  const [, setAlertBar] = alert;
  const [, setTab] = tab;
  const [userInfoVal, setUserInfo] = userInfo;

  const theme = useTheme();
  const [open, setOpen] = useState(false);

  const iconColor = { color: theme.palette.primary.contrastText };
  const dividerStyle = { height: 5 };

  const toggleDrawer = useCallback(() => setOpen((prev) => !prev), []);

  const isMobile = useMediaQuery("(max-width: 600px)");

  const menuItems = [
    { label: 'Inventory', icon: <InventoryIcon sx={iconColor} />, page: 'Inventory' },
    { label: 'Orders', icon: <ShoppingCartIcon sx={iconColor} />, page: 'Orders' },
  ];

  const adminItems = userInfoVal?.staff
    ? [{ label: 'Manage Users', icon: <PeopleIcon sx={iconColor} />, page: 'Manage Users' }]
    : [];

  const bottomItems = [
    { label: 'Developer Portal', icon: <DescriptionIcon sx={iconColor} />, page: 'API Docs' },
    { label: 'Account', icon: <AccountCircleIcon sx={iconColor} />, page: 'Account' },
    {
      label: 'Logout',
      icon: <LogoutIcon sx={iconColor} />,
      action: () => GetLogout(setLoading, setUserInfo, setTab, setAlertBar),
    },
  ];

  const renderListItems = (items) =>
    items.map(({ label, icon, page, action }) => (
      <ListItemButton
        key={label}
        onClick={() => (action ? action() : setTab({ page }))}
        sx={{
          minHeight: 48,
          justifyContent: open ? 'initial' : 'center',
          px: 2,
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 2 : 'auto',
            justifyContent: 'center',
            color: theme.palette.primary.contrastText,
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={label}
          sx={{
            opacity: open ? 1 : 0,
            color: theme.palette.primary.contrastText,
          }}
        />
      </ListItemButton>
    ));

  return (

    <Box>
      <AppHeader
        open={open}
        drawerWidth={drawerWidth}
        onToggleDrawer={toggleDrawer}
      />
      
    {/* Desktop / tablet: permanent (mini-variant) */}
      {!isMobile && (
        <Drawer variant="permanent" open={open}>
          <DrawerHeader>
            <IconButton onClick={toggleDrawer}>
              {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </DrawerHeader>

          <Divider sx={dividerStyle} />
          <List>{renderListItems(menuItems)}</List>
          <Divider sx={dividerStyle} />
          <List>{renderListItems(adminItems)}</List>
          {userInfoVal?.staff && <Divider sx={dividerStyle} />}

          <BottomContainer>
            <Divider sx={dividerStyle} />
            <List>{renderListItems(bottomItems)}</List>
          </BottomContainer>
        </Drawer>
      )}

      {/* Mobile: temporary (overlay) */}
      {isMobile && (
        <MuiDrawer
          variant="temporary"
          open={open}
          onClose={toggleDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              background: theme.palette.primary.dark,
              color: theme.palette.primary.contrastText,
            },
          }}
        >
          {/* If you did NOT offset top/height above, keep this Toolbar spacer */}
          <Toolbar />
          <List>{renderListItems(menuItems)}</List>
          <Divider sx={dividerStyle} />
          <List>{renderListItems(adminItems)}</List>
          {userInfoVal?.staff && <Divider sx={dividerStyle} />}
          <BottomContainer>
            <Divider sx={dividerStyle} />
            <List>{renderListItems(bottomItems)}</List>
          </BottomContainer>
        </MuiDrawer>
      )}

      {/* Main content that should NOT grow/shrink on mobile */}
      <Main open={open} isMobile={isMobile}>
        <Toolbar />
        {children}
      </Main>
    </Box>


  );
}